import cloneDeep from 'lodash.clonedeep';
import { AuthenticationReason, ThreeDsPreference } from '@mc-alberta/types';
/**
 * Returns the acquirer merchant ID and BIN, wherever available in the auth context.
 *
 * @param {AuthenticationContext} authenticationContext
 */
function getAcquirerData(authenticationContext) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const acquirerMerchantId = authenticationContext.acquirerMerchantId ||
        ((_a = authenticationContext.dpaData) === null || _a === void 0 ? void 0 : _a.acquirerId) ||
        ((_b = authenticationContext.dpaTransactionOptions) === null || _b === void 0 ? void 0 : _b.acquirerMerchantId) ||
        ((_e = (_d = (_c = authenticationContext.dpaTransactionOptions) === null || _c === void 0 ? void 0 : _c.threeDsInputData) === null || _d === void 0 ? void 0 : _d.acquirer) === null || _e === void 0 ? void 0 : _e.merchantID);
    const acquirerBIN = authenticationContext.acquirerBIN ||
        ((_f = authenticationContext.dpaData) === null || _f === void 0 ? void 0 : _f.acquirerBin) ||
        ((_g = authenticationContext.dpaTransactionOptions) === null || _g === void 0 ? void 0 : _g.acquirerBIN) ||
        ((_k = (_j = (_h = authenticationContext.dpaTransactionOptions) === null || _h === void 0 ? void 0 : _h.threeDsInputData) === null || _j === void 0 ? void 0 : _j.acquirer) === null || _k === void 0 ? void 0 : _k.bin);
    return { acquirerMerchantId, acquirerBIN };
}
/**
 * Maps transaction options from the given authentication context onto 3DS input data.
 *
 * @param {AuthenticationContext} authenticationContext
 * @returns {ThreeDsInputData}
 */
function mapThreeDsInputData(authenticationContext) {
    const dpaTransactionOptions = authenticationContext.dpaTransactionOptions;
    // create a new, clean instance of 3DS input data (to omit any unwanted params)
    const threeDsInputData = cloneDeep(dpaTransactionOptions.threeDsInputData) || {};
    if (authenticationContext.dpaData) {
        threeDsInputData.dpa = authenticationContext.dpaData;
    }
    const { acquirerMerchantId, acquirerBIN } = getAcquirerData(authenticationContext);
    if (acquirerBIN && acquirerMerchantId) {
        // map acquirer data onto 3DS input data, if available
        threeDsInputData.acquirer = {
            merchantID: acquirerMerchantId,
            bin: acquirerBIN
        };
    }
    return threeDsInputData;
}
/**
 * Maps a given AuthenticationContext such that its transaction options, if any, are properly
 * mapped onto values within the AuthenticationContext (including 3DS input data).
 *
 * @param {AuthenticationContext} authenticationContext
 * @returns {AuthenticationContext}
 */
export default function mapAuthenticationContext(authenticationContext) {
    var _a, _b;
    const _authenticationContext = cloneDeep(authenticationContext);
    if (!(_authenticationContext === null || _authenticationContext === void 0 ? void 0 : _authenticationContext.dpaTransactionOptions)) {
        return Object.assign(Object.assign({}, authenticationContext), getAcquirerData(authenticationContext));
    }
    const dpaTransactionOptions = Object.assign(Object.assign({}, cloneDeep(_authenticationContext.dpaTransactionOptions)), { paymentOptions: [].concat((_a = _authenticationContext
            .dpaTransactionOptions) === null || _a === void 0 ? void 0 : _a.paymentOptions), threeDsPreference: ThreeDsPreference.NONE });
    const dpaData = cloneDeep(_authenticationContext.dpaData);
    const isLowValue = _authenticationContext
        .authenticationReasons
        .find(r => [
        AuthenticationReason.ENROL_FINANCIAL_INSTRUMENT,
        AuthenticationReason.CONSUMER_IDENTITY_VALIDATION
    ].includes(r));
    if (isLowValue && !((_b = dpaTransactionOptions.transactionAmount) === null || _b === void 0 ? void 0 : _b.transactionAmount)) {
        // for LVLR transactions, force the amount to be zero if merchant did not already specify a value
        dpaTransactionOptions.transactionAmount = Object.assign(Object.assign({ transactionCurrencyCode: 'USD' }, dpaTransactionOptions.transactionAmount), { transactionAmount: 0 });
    }
    if (dpaData) {
        // DpaData introduced merchantCategoryCode/merchantCountryCode for the first time in EMVCo 1.3+
        // however it existed in DpaTransactionOptions in EMVCo 1.0+ - this provides fallbacks for these values
        dpaData.merchantCategoryCode = dpaData.merchantCategoryCode || dpaTransactionOptions.merchantCategoryCode;
        dpaData.merchantCountryCode = dpaData.merchantCountryCode || dpaTransactionOptions.merchantCountryCode;
    }
    dpaTransactionOptions.threeDsInputData = mapThreeDsInputData(authenticationContext);
    _authenticationContext.dpaTransactionOptions = dpaTransactionOptions;
    _authenticationContext.dpaData = dpaData;
    return Object.assign(Object.assign({}, _authenticationContext), getAcquirerData(authenticationContext));
}
//# sourceMappingURL=mapAuthenticationContext.js.map